import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Input, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import {
    Button as ZhButton,
    Checkbox as ZhCheckbox,
    Dialog as ZhDialog,
    InputNumber as ZhInputNumber,
    Table as ZhTable,
    type TableColumnProps,
    type TableRef
} from "zhihao-ui-react";

import { customColumnService } from "@/api/services/customColumn";
import { dashboardService } from "@/api/services/dashboardService";
import { DEFAULT_VALUE } from "@/config";
import type { CustomerVisitorVo, Page } from "@zhihaoscm/entity";
import { BUSSINESS_TYPE, FUNCTION_MODULE } from "@zhihaoscm/meta";
import { formatTimeEn } from "@/utils/formatter";
import "./FunctionalOperationUserVisitVolumeDetail.scss";

export interface FunctionalUserVisitVolumeQuery {
    page: number;
    size: number;
    scope?: number;
    beginTime?: string;
    endTime?: string;
    keyword?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

/** 沿用旧 Vue ProTable 用户访问量明细的分页条数选项。 */
export const FUNCTIONAL_USER_VISIT_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100];

export interface FunctionalOperationUserVisitVolumeDetailProps {
    searchParams: Omit<FunctionalUserVisitVolumeQuery, "page" | "size" | "keyword">;
    loadRows?: (params: FunctionalUserVisitVolumeQuery) => Promise<Page<CustomerVisitorVo>>;
    loadColumnConfig?: () => Promise<FunctionalUserVisitColumnSetting[] | undefined>;
    saveColumnConfig?: (settings: FunctionalUserVisitColumnSetting[]) => Promise<unknown>;
}

export interface FunctionalUserVisitColumnSetting {
    prop: string;
    isShow?: boolean;
    order?: number | string;
    width?: number | string;
}

/** 沿用旧 Vue 自定义列 10～1000px 的宽度边界，兼容历史字符串配置。 */
export function resolveFunctionalColumnWidth(
    defaultWidth: number | string | undefined,
    setting?: FunctionalUserVisitColumnSetting
): number | undefined {
    const fallback = Number(defaultWidth);
    const configured = setting?.width;
    if (configured === undefined || configured === "") return Number.isFinite(fallback) ? fallback : undefined;
    const width = Number(configured);
    if (!Number.isFinite(width)) return Number.isFinite(fallback) ? fallback : undefined;
    return Math.min(1000, Math.max(10, width));
}

const functionalRequiredColumnProps = new Set(["code", "mobileOrRealNameOrInstitutionName"]);

/** 判断旧 Vue 自定义列弹窗的全选状态，仅统计可编辑列。 */
export function areFunctionalColumnsAllVisible(settings: FunctionalUserVisitColumnSetting[]): boolean {
    return settings
        .filter(setting => !functionalRequiredColumnProps.has(setting.prop))
        .every(setting => setting.isShow !== false);
}

/** 切换旧 Vue 自定义列弹窗的全选状态，身份列始终保持展示。 */
export function setFunctionalColumnsVisibility(
    settings: FunctionalUserVisitColumnSetting[],
    isShow: boolean
): FunctionalUserVisitColumnSetting[] {
    return settings.map(setting =>
        functionalRequiredColumnProps.has(setting.prop) ? { ...setting, isShow: true } : { ...setting, isShow }
    );
}

/** 按旧 Vue 拖拽结果重排列配置，并重新生成连续的 order。 */
export function reorderFunctionalColumnSettings(
    settings: FunctionalUserVisitColumnSetting[],
    draggedProp: string,
    targetProp: string
): FunctionalUserVisitColumnSetting[] {
    const ordered = [...settings].sort((left, right) => Number(left.order ?? 0) - Number(right.order ?? 0));
    const draggedIndex = ordered.findIndex(setting => setting.prop === draggedProp);
    const targetIndex = ordered.findIndex(setting => setting.prop === targetProp);
    if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) return ordered;
    const [dragged] = ordered.splice(draggedIndex, 1);
    ordered.splice(targetIndex, 0, dragged);
    return ordered.map((setting, index) => ({ ...setting, order: index + 1 }));
}

/** 读取旧 Vue 功能运营分析页面保存的自定义列配置。 */
export async function loadFunctionalUserVisitColumnConfig() {
    const result = await customColumnService.query({ businessType: BUSSINESS_TYPE.FUNCTION.code });
    if (!result?.columnConfig) return undefined;
    try {
        return JSON.parse(result.columnConfig) as FunctionalUserVisitColumnSetting[];
    } catch {
        return undefined;
    }
}

/** 保存与旧 Vue 页面兼容的功能运营分析自定义列配置。 */
export function saveFunctionalUserVisitColumnConfig(settings: FunctionalUserVisitColumnSetting[]) {
    return customColumnService.save({
        businessType: BUSSINESS_TYPE.FUNCTION.code,
        columnConfig: JSON.stringify(settings)
    });
}

/** 按旧 Vue 自定义列规则解析显隐和顺序，用户身份两列始终保留。 */
export function resolveFunctionalVisibleColumnProps(
    columnProps: string[],
    settings: FunctionalUserVisitColumnSetting[] = []
): string[] {
    const requiredProps = new Set(["code", "mobileOrRealNameOrInstitutionName"]);
    const settingMap = new Map(settings.map(setting => [setting.prop, setting]));
    const required = columnProps.filter(prop => requiredProps.has(prop));
    const optional = columnProps
        .filter(prop => !requiredProps.has(prop) && settingMap.get(prop)?.isShow !== false)
        .sort((left, right) => {
            const leftOrder = Number(settingMap.get(left)?.order ?? Number.MAX_SAFE_INTEGER);
            const rightOrder = Number(settingMap.get(right)?.order ?? Number.MAX_SAFE_INTEGER);
            return leftOrder - rightOrder;
        });
    return [...required, ...optional];
}

/** 将功能运营分析页面筛选条件转换为旧 Vue 用户访问量分页请求参数。 */
export function buildFunctionalUserVisitQuery(
    searchParams: FunctionalOperationUserVisitVolumeDetailProps["searchParams"],
    page: number,
    size: number,
    keyword?: string,
    sort?: { sortKey?: string; sortOrder?: "asc" | "desc" }
): FunctionalUserVisitVolumeQuery {
    return {
        page,
        size,
        scope: searchParams.scope === 6 ? undefined : searchParams.scope,
        ...(searchParams.beginTime ? { beginTime: searchParams.beginTime } : {}),
        ...(searchParams.endTime ? { endTime: searchParams.endTime } : {}),
        keyword: keyword || undefined,
        ...(sort?.sortKey ? { sortKey: sort.sortKey } : {}),
        ...(sort?.sortOrder ? { sortOrder: sort.sortOrder } : {})
    };
}

/** 复用旧 Vue dashboardService.functionVisitorsDetail 接口。 */
export function loadFunctionalUserVisitVolume(params: FunctionalUserVisitVolumeQuery) {
    return dashboardService.functionVisitorsDetail(params);
}

const text = (value: unknown) =>
    value === null || value === undefined || value === "" ? DEFAULT_VALUE : String(value);

/** 沿用旧 Vue `.prop-text`，统一普通单元格的文字颜色和缺省值显示。 */
const cellText = (value: unknown) => <span className="text-[#000000e0]">{text(value)}</span>;

/** 按旧 Vue module1～module10 字段生成访问次数、停留时长和平均时长列。 */
const createModuleColumns = (): TableColumnProps<CustomerVisitorVo>[] =>
    FUNCTION_MODULE.options.flatMap(item => {
        const prefix = `module${item.value}`;
        return [
            {
                prop: `${prefix}TotalStayTime`,
                // 父级分组已展示模块名，子列完全复用 Vue 的短标题以避免表头自动测宽循环。
                label: "停留时长",
                minWidth: 124,
                align: "center",
                showOverflowTooltip: true,
                sortable: "custom",
                render: ({ row }) => {
                    const value = (row as Record<string, unknown>)[`${prefix}TotalStayTime`];
                    return cellText(value ? formatTimeEn(Number(value)) : DEFAULT_VALUE);
                }
            },
            {
                prop: `${prefix}Count`,
                label: "访问次数",
                minWidth: 124,
                align: "center",
                showOverflowTooltip: true,
                sortable: "custom",
                render: ({ row }) => cellText((row as Record<string, unknown>)[`${prefix}Count`])
            },
            {
                prop: `${prefix}AvgStayTime`,
                label: "平均单次停留时长",
                minWidth: 160,
                align: "center",
                showOverflowTooltip: true,
                sortable: "custom",
                render: ({ row }) => {
                    const value = (row as Record<string, unknown>)[`${prefix}AvgStayTime`];
                    return cellText(value ? formatTimeEn(Number(value)) : DEFAULT_VALUE);
                }
            }
        ];
    });

/** 从 module1～module10 字段中精确解析模块，避免 module10 被 module1 的前缀误匹配。 */
function getFunctionalModuleByProp(prop: string) {
    const moduleValue = /^module(\d+)(?:TotalStayTime|Count|AvgStayTime)$/.exec(prop)?.[1];
    return FUNCTION_MODULE.options.find(item => String(item.value) === moduleValue);
}

interface FunctionalColumnSettingDialogRow {
    key: string;
    label: string;
    settingProps: string[];
    isRequired: boolean;
    isChecked: boolean;
    width: number;
    order: number;
}

/** 将叶子列状态归并回旧 Vue 弹窗的身份列与模块分组行。 */
function createFunctionalColumnSettingDialogRows(
    settings: FunctionalUserVisitColumnSetting[],
    columns: TableColumnProps<CustomerVisitorVo>[]
): FunctionalColumnSettingDialogRow[] {
    const settingMap = new Map(settings.map(setting => [setting.prop, setting]));
    const columnMap = new Map(columns.map(column => [String(column.prop), column]));
    const requiredRows = settings
        .filter(setting => functionalRequiredColumnProps.has(setting.prop))
        .map(setting => {
            const column = columnMap.get(setting.prop);
            return {
                key: setting.prop,
                label: String(column?.label ?? setting.prop),
                settingProps: [setting.prop],
                isRequired: true,
                isChecked: setting.isShow !== false,
                width: resolveFunctionalColumnWidth(column?.width ?? column?.minWidth, setting) ?? 0,
                order: Number(setting.order ?? 0)
            };
        });
    const moduleRows = FUNCTION_MODULE.options
        .flatMap(module => {
            const settingProps = columns
                .map(column => String(column.prop))
                .filter(prop => getFunctionalModuleByProp(prop)?.value === module.value);
            const moduleSettings = settingProps.flatMap(prop => {
                const setting = settingMap.get(prop);
                return setting ? [setting] : [];
            });
            if (!moduleSettings.length) return [];

            const width = moduleSettings.reduce((total, setting) => {
                const column = columnMap.get(setting.prop);
                return total + (resolveFunctionalColumnWidth(column?.width ?? column?.minWidth, setting) ?? 0);
            }, 0);
            return [
                {
                    key: `module${module.value}`,
                    label: module.label,
                    settingProps,
                    isRequired: false,
                    isChecked: moduleSettings.every(setting => setting.isShow !== false),
                    width,
                    order: Math.min(...moduleSettings.map(setting => Number(setting.order ?? Number.MAX_SAFE_INTEGER)))
                }
            ];
        })
        .sort((left, right) => left.order - right.order);

    return [...requiredRows, ...moduleRows];
}

/** 将已筛选的功能模块叶子列恢复为旧 Vue 的分组表头，同时保留用户自定义顺序。 */
export function groupFunctionalUserVisitColumns(
    columns: TableColumnProps<CustomerVisitorVo>[],
    visibleProps: string[]
): TableColumnProps<CustomerVisitorVo>[] {
    const columnMap = new Map(columns.map(column => [String(column.prop), column]));
    const groupedColumns: TableColumnProps<CustomerVisitorVo>[] = [];
    const moduleGroups = new Map<string, TableColumnProps<CustomerVisitorVo>>();

    visibleProps.forEach(prop => {
        const column = columnMap.get(prop);
        if (!column) return;
        const module = getFunctionalModuleByProp(prop);
        if (!module) {
            groupedColumns.push(column);
            return;
        }

        const moduleKey = String(module.value);
        let group = moduleGroups.get(moduleKey);
        if (!group) {
            group = {
                label: module.label,
                minWidth: 408,
                align: "center",
                children: []
            };
            moduleGroups.set(moduleKey, group);
            groupedColumns.push(group);
        }
        (group.children as TableColumnProps<CustomerVisitorVo>[]).push(column);
    });

    return groupedColumns;
}

/** React 版用户访问量明细，保留旧 Vue 搜索、分页和 module1～module10 字段契约。 */
export function FunctionalOperationUserVisitVolumeDetail({
    searchParams,
    loadRows = loadFunctionalUserVisitVolume,
    loadColumnConfig,
    saveColumnConfig
}: FunctionalOperationUserVisitVolumeDetailProps) {
    const [rows, setRows] = useState<CustomerVisitorVo[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [keyword, setKeyword] = useState("");
    const [appliedKeyword, setAppliedKeyword] = useState<string>();
    const [sort, setSort] = useState<{ sortKey?: string; sortOrder?: "asc" | "desc" }>();
    const tableRef = useRef<TableRef<CustomerVisitorVo>>(null);
    const [columnSettings, setColumnSettings] = useState<FunctionalUserVisitColumnSetting[]>([]);
    const [columnDraftSettings, setColumnDraftSettings] = useState<FunctionalUserVisitColumnSetting[]>();
    const [columnDialogOpen, setColumnDialogOpen] = useState(false);
    const [draggedColumnProp, setDraggedColumnProp] = useState<string>();
    const [columnConfigLoading, setColumnConfigLoading] = useState(() => Boolean(loadColumnConfig));
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    useEffect(() => {
        if (!loadColumnConfig) {
            setColumnConfigLoading(false);
            return;
        }
        let disposed = false;
        setColumnConfigLoading(true);
        void loadColumnConfig()
            .then(settings => {
                if (!disposed && settings?.length) setColumnSettings(settings);
            })
            .catch(() => undefined)
            .finally(() => {
                if (!disposed) setColumnConfigLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [loadColumnConfig]);

    useEffect(() => {
        let disposed = false;
        setLoading(true);
        void loadRows(buildFunctionalUserVisitQuery(searchParams, page, pageSize, appliedKeyword, sort))
            .then(result => {
                if (disposed) return;
                setRows(result?.list || []);
                setTotal(result?.total || 0);
                setErrorMessage(undefined);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户访问量明细加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [appliedKeyword, loadRows, page, pageSize, searchParams, sort]);

    useEffect(() => {
        setPage(1);
    }, [searchParams]);

    const allColumns = useMemo<TableColumnProps<CustomerVisitorVo>[]>(
        () => [
            {
                prop: "code",
                label: "用户ID",
                minWidth: 170,
                showOverflowTooltip: true,
                render: ({ row }) =>
                    row.customerId && row.code ? (
                        <Link
                            className="cursor-pointer font-medium text-[#3370ff]"
                            to={`/lianYunUser/userInfo?userId=${encodeURIComponent(row.customerId)}`}
                        >
                            {row.code}
                        </Link>
                    ) : (
                        cellText(row.code)
                    )
            },
            {
                prop: "mobileOrRealNameOrInstitutionName",
                label: "手机号码/个人实名/机构名称",
                minWidth: 230,
                showOverflowTooltip: true,
                render: ({ row }) =>
                    cellText(
                        row.institutionName ||
                            row.realName ||
                            row.mobile ||
                            (row.deviceId ? `游客-${row.deviceId}` : null)
                    )
            },
            ...createModuleColumns()
        ],
        []
    );
    const defaultColumnSettings = useMemo<FunctionalUserVisitColumnSetting[]>(
        () =>
            allColumns.map((column, index) => ({
                prop: String(column.prop),
                isShow: true,
                order: index + 1,
                width: resolveFunctionalColumnWidth(column.width ?? column.minWidth)
            })),
        [allColumns]
    );
    // 历史配置可能只保存部分列，渲染和保存前按默认列补全，保持旧 Vue 配置弹窗完整展示。
    const activeColumnSettings = useMemo(
        () =>
            defaultColumnSettings
                .map(defaultSetting => {
                    const setting = {
                        ...defaultSetting,
                        ...columnSettings.find(item => item.prop === defaultSetting.prop)
                    };
                    // 历史配置即使误存身份列隐藏，也必须按旧 Vue 规则恢复为已选中。
                    return functionalRequiredColumnProps.has(setting.prop) ? { ...setting, isShow: true } : setting;
                })
                .sort((left, right) => Number(left.order ?? 0) - Number(right.order ?? 0)),
        [columnSettings, defaultColumnSettings]
    );
    const dialogColumnSettings = columnDraftSettings ?? activeColumnSettings;
    const dialogSettingRows = useMemo(
        () => createFunctionalColumnSettingDialogRows(dialogColumnSettings, allColumns),
        [allColumns, dialogColumnSettings]
    );
    const visibleColumnProps = useMemo(
        () =>
            resolveFunctionalVisibleColumnProps(
                allColumns.map(column => String(column.prop)),
                activeColumnSettings
            ),
        [activeColumnSettings, allColumns]
    );
    const leafColumns = useMemo(
        () =>
            visibleColumnProps.flatMap(prop => {
                const column = allColumns.find(item => String(item.prop) === prop);
                if (!column) return [];
                const setting = activeColumnSettings.find(item => item.prop === prop);
                const width = resolveFunctionalColumnWidth(column.width ?? column.minWidth, setting);
                return [width === undefined ? column : { ...column, width }];
            }),
        [activeColumnSettings, allColumns, visibleColumnProps]
    );
    const columns = useMemo(
        () => groupFunctionalUserVisitColumns(leafColumns, visibleColumnProps),
        [leafColumns, visibleColumnProps]
    );

    /** 打开旧 Vue ColSetting 等价弹窗时复制草稿，取消不会污染已应用配置。 */
    const openColumnSettings = () => {
        setColumnDraftSettings(activeColumnSettings);
        setColumnDialogOpen(true);
    };

    const updateColumnVisibility = (props: string[], isShow: boolean) => {
        setColumnDraftSettings(current => {
            const settings = current ?? activeColumnSettings;
            return settings.map(setting => (props.includes(setting.prop) ? { ...setting, isShow } : setting));
        });
    };

    /** 按旧 Vue 分组宽度比例分配子列宽，并沿用 10～1000px 的输入边界。 */
    const updateColumnWidth = (props: string[], width: number | string) => {
        setColumnDraftSettings(current => {
            const settings = current ?? activeColumnSettings;
            const requestedWidth = resolveFunctionalColumnWidth(undefined, { prop: "", width });
            if (requestedWidth === undefined) return settings;

            const columnMap = new Map(allColumns.map(column => [String(column.prop), column]));
            const settingMap = new Map(settings.map(setting => [setting.prop, setting]));
            const currentWidths = props.map(prop => {
                const setting = settingMap.get(prop);
                const column = columnMap.get(prop);
                return resolveFunctionalColumnWidth(column?.width ?? column?.minWidth, setting) ?? 0;
            });
            const currentTotal = currentWidths.reduce((total, value) => total + value, 0) || props.length;
            let remainingWidth = requestedWidth;
            const nextWidths = currentWidths.map((currentWidth, index) => {
                if (index === currentWidths.length - 1) return remainingWidth;
                const nextWidth = Math.ceil((currentWidth / currentTotal) * requestedWidth);
                remainingWidth -= nextWidth;
                return nextWidth;
            });
            const nextWidthByProp = new Map(props.map((prop, index) => [prop, nextWidths[index]]));
            return settings.map(setting =>
                nextWidthByProp.has(setting.prop) ? { ...setting, width: nextWidthByProp.get(setting.prop) } : setting
            );
        });
    };

    /** 拖动整个功能模块行，并将行顺序展开为模块内连续的叶子列顺序。 */
    const moveColumn = (targetKey: string) => {
        if (!draggedColumnProp || draggedColumnProp === targetKey) return;
        setColumnDraftSettings(current => {
            const settings = current ?? activeColumnSettings;
            const rows = createFunctionalColumnSettingDialogRows(settings, allColumns);
            const draggedIndex = rows.findIndex(row => row.key === draggedColumnProp);
            const targetIndex = rows.findIndex(row => row.key === targetKey);
            if (draggedIndex < 0 || targetIndex < 0) return settings;

            const [draggedRow] = rows.splice(draggedIndex, 1);
            rows.splice(targetIndex, 0, draggedRow);
            const nextProps = rows.flatMap(row => row.settingProps);
            const settingMap = new Map(settings.map(setting => [setting.prop, setting]));
            return nextProps.flatMap((prop, index) => {
                const setting = settingMap.get(prop);
                return setting ? [{ ...setting, order: index + 1 }] : [];
            });
        });
        setDraggedColumnProp(undefined);
    };

    /** 取消弹窗草稿，恢复打开弹窗前已经应用的列配置。 */
    const cancelColumnSettings = () => {
        setColumnDraftSettings(undefined);
        setDraggedColumnProp(undefined);
        setColumnDialogOpen(false);
    };

    const search = () => {
        setPage(1);
        setAppliedKeyword(keyword.trim() || undefined);
    };

    /** 对齐旧 Vue clear 事件，清空关键词后立即按无关键词条件查询。 */
    const clearKeyword = () => {
        setKeyword("");
        setPage(1);
        setAppliedKeyword(undefined);
    };

    const reset = () => {
        setKeyword("");
        setAppliedKeyword(undefined);
        setSort(undefined);
        setPage(1);
        tableRef.current?.clearSort();
    };

    /** 保存当前列配置；未接入持久化适配器时仍保留本地列设置行为。 */
    const persistColumns = async () => {
        const settings = dialogColumnSettings;
        try {
            if (saveColumnConfig) await saveColumnConfig(settings);
            setColumnSettings(settings);
            setColumnDraftSettings(undefined);
            setDraggedColumnProp(undefined);
            setColumnDialogOpen(false);
        } catch {
            setErrorMessage("自定义列保存失败");
        }
    };

    return (
        <section
            aria-label="用户访问量明细"
            className="functional-operation-user-visit-volume-detail rounded-lg bg-white p-5"
        >
            {/* Vue 标题直接跟随卡片内边距，16px 间距放在标题与工具栏之间。 */}
            <div
                aria-busy={loading || columnConfigLoading}
                className="functional-operation-user-visit-table-viewport relative overflow-x-auto"
            >
                {!columnConfigLoading ? (
                    <div className="[&_.ant-table-tbody_.ant-table-cell]:whitespace-nowrap">
                        <ZhTable<CustomerVisitorVo>
                            ref={tableRef}
                            rowKey={row => String(row.customerId || row.deviceId || row.code)}
                            data={rows}
                            columns={columns}
                            // 旧 Vue 功能运营分析未固定身份列；避免 zhihao-ui-react fixed 列副本触发表头测宽循环。
                            className="functional-operation-user-visit-table w-full"
                            border={false}
                            rowStyle={{ height: 42 }}
                            rowClassName="[&:hover>td]:bg-[#f5f7fa]"
                            tooltipEffect="light"
                            tooltipOptions={{
                                showArrow: false,
                                placement: "bottom-start",
                                offset: -20
                            }}
                            pagination={false}
                            isShowSearchBtn={false}
                            isShowResetBtn={false}
                            tableHeader={
                                <div className="admin-table-composed-header">
                                    <div className="flex items-center">
                                        <span
                                            aria-hidden="true"
                                            data-testid="functional-operation-user-visit-panel-title-mark"
                                            className="mr-1.5 inline-block h-3.5 w-0.5 shrink-0 rounded bg-[#3370ff]"
                                        />
                                        <h2
                                            className="m-0 text-sm font-medium leading-[14px] text-[#000000e0]"
                                            style={{ fontSize: 14, fontWeight: 500, lineHeight: "14px" }}
                                        >
                                            用户访问量明细
                                        </h2>
                                    </div>
                                    <div
                                        data-testid="functional-operation-user-visit-toolbar"
                                        className="mt-[16px] flex items-center justify-between"
                                    >
                                        <Space wrap>
                                            <Input
                                                aria-label="用户访问量关键词"
                                                maxLength={50}
                                                className="functional-operation-user-visit-keyword min-w-[280px] shrink-0 rounded-[6px] bg-[#f5f5f5] text-xs shadow-none [&:hover]:shadow-[0_0_0_1px_#3370ff_inset] [&:focus-within]:shadow-[0_0_0_1px_#3370ff_inset] [&_.ant-input-affix-wrapper]:bg-[#f5f5f5] [&_.ant-input-affix-wrapper]:rounded-[6px] [&_.ant-input]:bg-[#f5f5f5] [&_.ant-input]:text-xs [&_.ant-input]:shadow-none"
                                                style={{ width: 280 }}
                                                placeholder="请输入用户ID/个人实名/机构名称/手机号搜索"
                                                value={keyword}
                                                allowClear
                                                onChange={event => setKeyword(event.target.value)}
                                                onClear={clearKeyword}
                                                onPressEnter={search}
                                            />
                                            <Button
                                                type="primary"
                                                className="rounded-[6px] px-2 text-xs"
                                                onClick={search}
                                            >
                                                <span aria-hidden="true" className="iconfont icon-Frame-15 mr-[3px]" />
                                                搜索
                                            </Button>
                                        </Space>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                aria-label="重置"
                                                title="重置"
                                                className="functional-operation-user-visit-reset h-8 w-9 rounded-[6px] border-0 bg-[#f5f5f5] p-0 text-xs hover:bg-[#e5e6eb]"
                                                onClick={reset}
                                            >
                                                <span aria-hidden="true" className="iconfont icon-Frame8" />
                                            </Button>
                                        </div>
                                    </div>
                                    {/* Vue 将自定义列按钮锚定在表格表头右侧，不与搜索及重置操作混排。 */}
                                    <div
                                        data-testid="functional-operation-column-settings-anchor"
                                        className="functional-operation-column-settings-anchor w-full"
                                    >
                                        <Button
                                            aria-label="自定义列"
                                            title="自定义列"
                                            className="functional-operation-column-settings-button z-[100] cursor-pointer"
                                            style={{
                                                position: "absolute",
                                                top: 16,
                                                right: 0,
                                                width: 40,
                                                height: 40,
                                                padding: 0
                                            }}
                                            type="text"
                                            onClick={openColumnSettings}
                                        >
                                            <span aria-hidden="true" className="iconfont icon-a-Frame21 text-xl" />
                                        </Button>
                                    </div>
                                    {errorMessage ? (
                                        <Alert className="my-4" message={errorMessage} role="alert" type="error" />
                                    ) : null}
                                </div>
                            }
                            append={
                                <div className="admin-table-composed-pagination">
                                    <ZhPagination
                                        current={page}
                                        pageSize={pageSize}
                                        pageSizeOptions={FUNCTIONAL_USER_VISIT_PAGE_SIZE_OPTIONS}
                                        showSizeChanger
                                        total={total}
                                        hideOnSinglePage
                                        onChange={(nextPage, nextPageSize) => {
                                            setPage(nextPageSize !== pageSize ? 1 : nextPage);
                                            setPageSize(nextPageSize);
                                        }}
                                    />
                                </div>
                            }
                            onSortChange={sorter => {
                                setPage(1);
                                setSort(
                                    sorter.prop && sorter.order
                                        ? {
                                              sortKey: sorter.prop,
                                              sortOrder: sorter.order === "ascending" ? "asc" : "desc"
                                          }
                                        : undefined
                                );
                            }}
                        />
                    </div>
                ) : null}
            </div>
            <ZhDialog
                open={columnDialogOpen}
                modalClass="functional-operation-column-setting-modal"
                alignCenter
                header={
                    <div className="functional-operation-column-setting-header flex items-center">
                        <span className="text-base font-medium leading-4 text-[#000000e0]">自定义列</span>
                        <span
                            data-testid="functional-column-dialog-subtitle"
                            className="ml-4 text-sm font-normal leading-[14px] text-[#8c8c8c]"
                        >
                            勾选需要展示的列，拖动列名进行排序
                        </span>
                    </div>
                }
                width={687}
                footer={
                    <div
                        data-testid="functional-column-dialog-footer"
                        className="functional-column-setting-footer flex items-center p-5"
                    >
                        <ZhCheckbox
                            aria-label="全选"
                            checked={areFunctionalColumnsAllVisible(dialogColumnSettings)}
                            onChange={event =>
                                setColumnDraftSettings(
                                    setFunctionalColumnsVisibility(dialogColumnSettings, event.target.checked)
                                )
                            }
                        >
                            全选
                        </ZhCheckbox>
                        <div className="ml-auto">
                            <Space>
                                <ZhButton onClick={cancelColumnSettings}>取消</ZhButton>
                                <ZhButton onClick={() => setColumnDraftSettings(defaultColumnSettings)}>
                                    恢复默认
                                </ZhButton>
                                <ZhButton type="primary" onClick={() => void persistColumns()}>
                                    保存
                                </ZhButton>
                            </Space>
                        </div>
                    </div>
                }
                onCancel={cancelColumnSettings}
            >
                <div className="functional-column-setting-table-wrapper">
                    <table aria-label="自定义列设置" className="functional-column-setting-table">
                        <tbody>
                            {dialogSettingRows.map(row => {
                                return (
                                    <tr
                                        key={row.key}
                                        className="functional-column-setting-row"
                                        draggable
                                        onDragStart={() => setDraggedColumnProp(row.key)}
                                        onDragOver={event => event.preventDefault()}
                                        onDrop={() => moveColumn(row.key)}
                                    >
                                        <td className="functional-column-setting-selection">
                                            <ZhCheckbox
                                                aria-label={row.label}
                                                checked={row.isChecked}
                                                disabled={row.isRequired}
                                                onChange={event =>
                                                    updateColumnVisibility(row.settingProps, event.target.checked)
                                                }
                                            />
                                        </td>
                                        <td className="functional-column-setting-name">
                                            <span>{row.label}</span>
                                            {row.isRequired ? <span className="ml-10">(必选)</span> : null}
                                            <span
                                                aria-label="拖动排序"
                                                className="iconfont icon-yidong ml-3 cursor-move text-xl text-[#3370ff]"
                                                title="拖动排序"
                                            />
                                        </td>
                                        <td className="functional-column-setting-width">
                                            <div className="functional-column-setting-width-content">
                                                <span className="functional-column-setting-width-label">宽度</span>
                                                {row.isRequired ? (
                                                    <span
                                                        aria-label={`当前宽度-${row.label}`}
                                                        className="functional-column-setting-width-readonly ml-8 text-sm font-normal leading-8 text-[#000000e0]"
                                                    >
                                                        {row.width} px
                                                    </span>
                                                ) : (
                                                    <div className="relative ml-4 flex h-[26px] w-[90px]">
                                                        <ZhInputNumber
                                                            aria-label={`列宽-${row.label}`}
                                                            className="h-[26px] w-[90px]"
                                                            value={row.width}
                                                            min={10}
                                                            max={1000}
                                                            precision={0}
                                                            onChange={value =>
                                                                updateColumnWidth(row.settingProps, value)
                                                            }
                                                        />
                                                        <span className="pointer-events-none absolute right-2 top-0 text-sm font-normal leading-[26px] text-[#bfbfbfe0]">
                                                            px
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </ZhDialog>
        </section>
    );
}
